import type { ServicePageConfig } from "../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../package-pricing";
import { getCityMunicipalityList } from "../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");

export const danderydPages: ServicePageConfig[] = [
  {
    slug: "bortforsling-danderyd",
    serviceCategory: "bortforsling",
    city: "stockholm",

    meta: {
      title: "Bortforsling Danderyd – Snabb Hämtning | Ungdomskraft",
      description:
        "Boka bortforsling i Danderyd. Vi hämtar möbler, vitvaror och skräp i Djursholm, Stocksund, Enebyberg och Mörby. Fast pris, ofta samma dag. Försäkrat och tryggt.",
      h1: "Bortforsling Danderyd",
    },

    hero: {
      heading: "Bortforsling i Danderyd",
      intro:
        "Boka bortforsling i Danderyd – vi hämtar möbler, vitvaror och skrymmande föremål i hela kommunen, från villorna i Djursholm, Stocksund och Enebyberg till lägenheterna kring Mörby centrum. Snabb bokning, fast pris och ofta tillgängliga redan samma dag. Över 50 000 slutförda uppdrag i Stockholmsregionen.",
      heroImage: "/hero-bortforsling.webp",
      heroImageAlt:
        "Ungdomskraft hämtar möbler vid bortforsling i Danderyd – ung medarbetare lastar skåpbil",
    },

    itemsSection: {
      heading: "Saker vi bortforslar i Danderyd",
      items: [
        {
          name: "Återvinning",
          description: "Glas, metall, papper och annat återvinningsbart",
          image: "/recycling-bin.webp",
          alt: "Återvinning som hämtas vid bortforsling i Danderyd och körs till Hagby ÅVC",
        },
        {
          name: "Kartonger",
          description: "Flyttkartonger och wellpappkartonger",
          image: "/box.webp",
          alt: "Flyttkartonger som forslas bort från villa i Djursholm, Danderyd",
        },
        {
          name: "Möbler",
          description: "Vardagsrum, sovrum, kontor och andra hemmöbler",
          image: "/furniture.webp",
          alt: "Soffor och hemmöbler vid bortforsling i Stocksund, Danderyd",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, ljudsystem och annan elektronik",
          image: "/electronics.webp",
          alt: "Elektronik och TV-apparater som bortforslas i Mörby, Danderyd",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
          image: "/appliances.webp",
          alt: "Kylskåp och vitvaror som hämtas vid bortforsling i Enebyberg, Danderyd",
        },
        {
          name: "Träningsutrustning",
          description:
            "Löpband, cyklar, vikter och annan träningsutrustning",
          image: "/fitness-equipment.webp",
          alt: "Löpband och träningsutrustning vid bortforsling i Kevinge, Danderyd",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bortforsling i Danderyd – snabbt, smidigt och ansvarsfullt",
      mainText:
        "Danderyd är en av Sveriges mest villatäta kommuner, med fyra kommundelar – Danderyd, Djursholm, Stocksund och Enebyberg. Djursholm räknas som ett av landets mest exklusiva villaområden, och även Stocksund och Enebyberg är utpräglade villasamhällen med stora tomter. Undantaget är lägenhetsbebyggelsen kring Mörby centrum, Mörbyskogen och Kevinge. Den blandningen gör bortforsling till olika utmaningar: i de stora villorna handlar det ofta om att tömma garage, vind, källare och uthus, medan det i lägenheterna kring Mörby snarare handlar om trapphus och hiss. Med Ungdomskraft slipper du allt krångel oavsett vilket. Vi skickar unga, starka medarbetare med skåpbil direkt till din adress.\n\nVårt team hanterar hela kedjan: vi bär ut föremålen från din villa, lägenhet eller ditt förråd, lastar i bilen och kör till närmaste återvinningscentral. Danderyd är en av delägarkommunerna i SÖRAB, och eftersom kommunen saknar egen återvinningscentral kör vi det mesta till Hagby ÅVC i Täby strax norrut – SÖRAB:s största och mest besökta station. Du behöver inte tänka på sortering, parkering eller tunga lyft. Vi tar hand om allt.\n\nTack vare att E18 går genom Danderyd, att röda linjens tunnelbana slutar vid Danderyds sjukhus och Mörby centrum, och att Roslagsbanan passerar Stocksund, Djursholm och Enebyberg, tar vi oss snabbt mellan kommundelarna. Det gör att vi ofta kan vara på plats redan samma dag du bokar. Våra medarbetare är vana vid både de stora villatomterna i Djursholm och de smala grusvägarna i Enebyberg – och har utrustningen som krävs: bärbälten, skyddsfiltar och lastramper som skyddar både dina ägodelar och trapphuset under hela processen.",
      preparationHeading: "Förbered inför din bortforsling i Danderyd",
      preparationText:
        "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen och hålla nere kostnaden finns det några enkla saker du kan göra:\n\nRensa en fri väg (ungefär 1 meter bred) från entrén till föremålen som ska bort. Bor du i villa i Djursholm, Stocksund eller Enebyberg är det smidigt om vi kan backa in på uppfarten – meddela oss om grinden eller garageporten behöver öppnas, och om uppfarten är grusad eller brant. Bor du i lägenhet kring Mörby centrum eller Mörbyskogen, kontrollera om det finns en lastzon, innergård eller gästparkering nära porten som vi kan använda för lastning.\n\nSamla gärna sakerna på ett ställe om möjligt, till exempel i hallen, garaget eller uthuset. Du behöver inte demontera möbler – det ingår i vår tjänst om det behövs. Farligt avfall som färgrester, batterier och kemikalier kan vi tyvärr inte ta med, men sådant lämnar du gratis som Danderydsbo på Hagby ÅVC eller via kommunens mobila återvinningscentral.",
      additionalSections: [
        {
          heading: "Kommundelar och områden vi täcker i Danderyd",
          content:
            "Vi utför bortforsling i hela Danderyds kommun – från de exklusiva villakvarteren till lägenheterna kring Mörby. Oavsett om du bor i en villa i Djursholm, ett hus i Stocksund, en villa i Enebyberg eller en lägenhet i Mörbyskogen kan vi vara på plats, ofta redan samma dag.\n\nI Djursholm – med kvarter som Germania, Vendevägen och området kring Djursholms slott – är vi vana vid stora villor med flera plan, vindar och uthus. I Stocksund, vid Stocksundet, och i norra Enebyberg möter vi liknande villabebyggelse med rymliga garage och förråd. I dessa områden planerar vi alltid hur skåpbilen ska komma nära entrén, även på grusade eller branta uppfarter.\n\nI den centrala kommundelen Danderyd – kring Mörby centrum, Mörbyskogen, Kevinge och Sätra – har vi stor vana vid lägenhetshämtningar med hiss och trapphus, nära tunnelbanans ändstation. Tack vare närheten till E18, röda linjen och Roslagsbanan planerar vi alltid rutten i förväg så att du slipper vänta. Inga extra avgifter för någon kommundel.",
        },
        {
          heading: "Återvinning och miljöansvar i Danderyd",
          content:
            "En viktig del av vår bortforsling i Danderyd är att allt hanteras på ett miljövänligt sätt. Vi sorterar materialet och kör det till godkända återvinningscentraler i området. Danderyd är en av delägarkommunerna i SÖRAB, och de lösningar vi använder mest för uppdrag i kommunen är:\n\nHagby ÅVC i Täby – SÖRAB:s största och mest besökta återvinningscentral, strax norr om Danderyd, där sorterat grovavfall, trädgårdsavfall, elavfall och farligt avfall tas emot, liksom föremål och textil för återbruk. Den mobila återvinningscentralen – som besöker olika platser i Danderyd enligt ett rullande schema för mindre mängder. Vi väljer alltid den lösning som ligger närmast din adress för att hålla restiden – och därmed kostnaden – nere.\n\nDet som kan återanvändas skänker vi vidare till organisationer som Myrorna och Stadsmissionen. På så sätt bidrar din bortforsling till cirkulär ekonomi – sakerna får ett nytt liv istället för att hamna på tippen.\n\nOm sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Varför boka bortforsling i Danderyd med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi känner Danderyds kommundelar, från de stora villatomterna i Djursholm och Stocksund till lägenheterna kring Mörby centrum, och vet att Hagby ÅVC ligger strax norrut. Det gör att vi planerar effektiva rutter och håller nere kostnaden för dig.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Stockholmsregionen och E18 går genom Danderyd. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för någon kommundel i Danderyd, och inga offertförfrågningar. Boka på två minuter.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Ditt trapphus, din villauppfart, dina väggar och dina ägodelar är skyddade under hela processen.",
            },
            {
              label: "Miljövänlig hantering –",
              text: "Vi sorterar och återvinner allt vi kan via Hagby ÅVC och SÖRAB:s anläggningar. Det som går att donera skänks vidare. Du kan ha rätt till RUT-avdrag för transport till återbruk.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bortforsling i Danderyd?",
        answer:
          "Priset beror på volym, vikt och tid. Vår minsta kategori (Extra Liten) startar på 249 kr i grundavgift, och ett genomsnittligt uppdrag kostar runt 499–2 495 kr beroende på mängden. Du ser det exakta priset direkt vid bokning – inga dolda avgifter och inga tillägg för någon kommundel i Danderyd.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling i Danderyd?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Hur snabbt kan ni komma för bortforsling i Danderyd?",
        answer:
          "Vi har skåpbilar och personal i hela Stockholmsregionen och E18 går genom Danderyd, så vi tar oss snabbt mellan kommundelarna. Ofta kan vi vara på plats redan samma dag, och vid akuta behov ibland inom 60 minuter. Boka online och välj den tid som passar dig.",
      },
      {
        question: "Ingår det en skåpbil vid bortforsling i Danderyd?",
        answer:
          "Ja, vi kommer alltid med skåpbil och personal. Du behöver inte ordna eget fordon – vi sköter allt från bärning till transport och bortkörning till närmaste återvinningscentral.",
      },
      {
        question: "Hämtar ni från villa, garage och uthus i Danderyd?",
        answer:
          "Ja, en stor del av våra uppdrag i Danderyd är villahämtningar i Djursholm, Stocksund och Enebyberg. Vi backar in på uppfarten där det går och tömmer garage, källare, vind och uthus. Meddela oss bara om en grind eller garageport behöver öppnas, eller om uppfarten är grusad eller brant.",
      },
      {
        question: "Kan ni bära ner saker från lägenhet i trappa i Danderyd?",
        answer:
          "Absolut. Vi är vana vid både trapphus och hissar i flerbostadshusen kring Mörby centrum, Mörbyskogen och Kevinge. Vi bär ner möbler, vitvaror och annat oavsett våning – inga extra avgifter för trappor.",
      },
      {
        question: "Kan ni demontera möbler innan bortforsling?",
        answer:
          "Ja, vi demonterar möbler på plats om det behövs för att få ut dem genom dörrar eller nedför trappor. Det ingår i tjänsten utan extra kostnad.",
      },
      {
        question: "Är bortforslingen i Danderyd försäkrad?",
        answer:
          "Ja, alla uppdrag är försäkrade genom Trygg Hansa. Det innebär att din fastighet, ditt trapphus, din villauppfart och dina ägodelar är skyddade under hela processen – från lyft till transport.",
      },
      {
        question: "Vart kör ni avfallet efter bortforsling i Danderyd?",
        answer:
          "Vi sorterar allt material och kör det till närmaste godkända återvinningscentral. Danderyd saknar egen ÅVC men är delägare i SÖRAB, så avfallet körs oftast till Hagby ÅVC i Täby strax norrut. Det som kan återanvändas skänks till Myrorna eller Stadsmissionen.",
      },
      {
        question: "Kan jag boka bortforsling i Danderyd på helgen?",
        answer:
          "Ja, vi erbjuder bortforsling alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst direkt i vår bokning.",
      },
      {
        question: "Kan ni hämta saker från källare, vind eller uthus i Danderyd?",
        answer:
          "Ja, vi hämtar föremål från alla utrymmen – källare, vindsutrymmen, uthus, förråd och garage. Vi är vana vid de stora villaförråden i Djursholm och Stocksund såväl som lägenhetsförråden kring Mörby.",
      },
      {
        question: "Kan ni hjälpa till med bortforsling vid dödsbo i Danderyd?",
        answer:
          "Ja, vi hjälper ofta till med tömning och bortforsling vid dödsbo och flytt i Danderyds villor. Vi hanterar hela hus med varsamhet, sorterar det som ska återvinnas och ser till att det som går att återbruka kommer till nytta.",
      },
      {
        question: "Vilka kommundelar i Danderyd täcker ni?",
        answer:
          "Vi utför bortforsling i hela Danderyd – kommundelen Danderyd med Mörby centrum, Mörbyskogen och Kevinge, samt Djursholm, Stocksund och Enebyberg. Inga extra avgifter för någon kommundel.",
      },
      {
        question: "Tar ni hand om farligt avfall vid bortforsling i Danderyd?",
        answer:
          "Vi kan tyvärr inte transportera farligt avfall som färg, kemikalier, batterier eller asbest. Sådant lämnar du gratis som Danderydsbo på Hagby ÅVC eller via kommunens mobila återvinningscentral.",
      },
      {
        question: "Vilka kommuner runt Danderyd utför ni bortforsling i?",
        answer: `Vi täcker hela Stockholmsregionen, inklusive följande kommuner: ${stockholmKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Boka din bortforsling i Danderyd i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Danderyd kommer till dig och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft, trånga trapphus, smala hissar och fulla villagarage och uthus – du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling i Danderyd idag",
      ctaText:
        "Fyll i formuläret och få pris direkt – inga offertförfrågningar. Smidig bortforsling i hela Danderyds kommun.",
    },

    en: {
      slug: "junk-removal-danderyd",
      meta: {
        title: "Junk Removal Danderyd – Fast Pickup | Ungdomskraft",
        description:
          "Book junk removal in Danderyd. We pick up furniture, appliances and bulky items in Djursholm, Stocksund, Enebyberg and Mörby. Fixed price, often same day. Insured.",
        h1: "Junk Removal Danderyd",
      },
      hero: {
        heading: "Junk Removal in Danderyd",
        intro:
          "Book junk removal in Danderyd – we collect furniture, appliances and bulky items across the whole municipality, from the villas in Djursholm, Stocksund and Enebyberg to the apartments around Mörby centrum. Quick booking, fixed price and often available the same day. Over 50,000 completed jobs in the Stockholm region.",
        heroImageAlt:
          "Ungdomskraft collecting furniture during junk removal in Danderyd – young worker loading a van",
      },
      itemsSection: {
        heading: "Items we remove in Danderyd",
        items: [
          {
            name: "Recycling",
            description: "Glass, metal, paper and other recyclables",
            image: "/recycling-bin.webp",
            alt: "Recyclables collected during junk removal in Danderyd and driven to Hagby recycling centre",
          },
          {
            name: "Boxes",
            description: "Moving boxes and cardboard boxes",
            image: "/box.webp",
            alt: "Moving boxes removed from a villa in Djursholm, Danderyd",
          },
          {
            name: "Furniture",
            description: "Living room, bedroom, office and other home furniture",
            image: "/furniture.webp",
            alt: "Sofas and home furniture during junk removal in Stocksund, Danderyd",
          },
          {
            name: "Electronics",
            description: "TVs, computers, sound systems and other electronics",
            image: "/electronics.webp",
            alt: "Electronics and TVs removed in Mörby, Danderyd",
          },
          {
            name: "Appliances",
            description: "Fridges, washing machines, dryers and other appliances",
            image: "/appliances.webp",
            alt: "Fridges and appliances collected during junk removal in Enebyberg, Danderyd",
          },
          {
            name: "Fitness Equipment",
            description: "Treadmills, bikes, weights and other exercise equipment",
            image: "/fitness-equipment.webp",
            alt: "Treadmill and fitness equipment during junk removal in Kevinge, Danderyd",
          },
        ],
      },
      seoContent: {
        mainHeading: "Junk removal in Danderyd – fast, hassle-free and responsible",
        mainText:
          "Danderyd is one of Sweden's most villa-dense municipalities, with four districts – Danderyd, Djursholm, Stocksund and Enebyberg. Djursholm is considered one of the country's most exclusive villa areas, and Stocksund and Enebyberg are also distinct villa communities with large plots. The exception is the apartment housing around Mörby centrum, Mörbyskogen and Kevinge. That mix makes junk removal a different challenge: in the large villas it is often about clearing garages, attics, basements and outbuildings, while in the apartments around Mörby it is more about stairwells and elevators. With Ungdomskraft, you skip the hassle either way. We send young, strong workers with a van directly to your address.\n\nOur team handles the entire chain: we carry items out of your villa, apartment or storage unit, load them into the van and drive to the nearest recycling centre. Danderyd is one of the part-owner municipalities of SÖRAB, and since the municipality has no recycling centre of its own, we take most things to Hagby ÅVC in Täby just to the north – SÖRAB's largest and most-visited station. You don't need to worry about sorting, parking or heavy lifting. We take care of everything.\n\nBecause the E18 runs through Danderyd, the red metro line ends at Danderyds sjukhus and Mörby centrum, and the Roslagsbanan railway passes Stocksund, Djursholm and Enebyberg, we move quickly between districts. This means we can often be on site the same day you book. Our workers are used to both the large villa plots of Djursholm and the narrow gravel roads of Enebyberg – and have the right equipment: carrying belts, protective blankets and loading ramps that protect both your belongings and the stairwell throughout the process.",
        preparationHeading: "Preparing for your junk removal in Danderyd",
        preparationText:
          "You don't really need to do much at all – our young workers handle the heavy work. But if you want to speed things up and keep the cost down, there are a few simple things you can do:\n\nClear a path (about 1 metre wide) from the entrance to the items that need to go. If you live in a villa in Djursholm, Stocksund or Enebyberg, it helps if we can reverse onto the driveway – let us know if a gate or garage door needs opening, and whether the driveway is gravelled or steep. If you live in an apartment around Mörby centrum or Mörbyskogen, check whether there is a loading zone, courtyard or visitor parking near the entrance we can use.\n\nGather the items in one place if possible, for example in the hallway, garage or outbuilding. You don't need to disassemble furniture – we include that in our service if needed. We cannot take hazardous waste such as paint, batteries and chemicals, but as a Danderyd resident you can drop those off for free at Hagby ÅVC or via the municipality's mobile recycling centre.",
        additionalSections: [
          {
            heading: "Districts and areas we cover in Danderyd",
            content:
              "We perform junk removal across the entire municipality of Danderyd – from the exclusive villa neighbourhoods to the apartments around Mörby. Whether you live in a villa in Djursholm, a house in Stocksund, a villa in Enebyberg or an apartment in Mörbyskogen, we can be on site, often the same day.\n\nIn Djursholm – with quarters such as Germania, Vendevägen and the area around Djursholm Castle – we are used to large multi-storey villas with attics and outbuildings. In Stocksund, by Stocksundet, and in northern Enebyberg we meet similar villa housing with spacious garages and storage rooms. In these areas we always plan how the van can get close to the entrance, even on gravelled or steep driveways.\n\nIn the central district of Danderyd – around Mörby centrum, Mörbyskogen, Kevinge and Sätra – we are highly experienced with apartment pickups via elevator and stairwell, close to the metro terminus. Thanks to the proximity of the E18, the red metro line and the Roslagsbanan, we always plan the route in advance so you don't have to wait. No extra charges for any district.",
          },
          {
            heading: "Recycling and environmental responsibility in Danderyd",
            content:
              "An important part of our junk removal in Danderyd is that everything is handled in an environmentally responsible way. We sort the materials and drive them to approved recycling centres in the area. Danderyd is one of the part-owner municipalities of SÖRAB, and the solutions we use most for jobs in the municipality are:\n\nHagby ÅVC in Täby – SÖRAB's largest and most-visited recycling centre, just north of Danderyd, which receives sorted bulky waste, garden waste, e-waste and hazardous waste, as well as items and textiles for reuse. The mobile recycling centre – which visits different locations in Danderyd on a rolling schedule for smaller amounts. We always choose the solution nearest your address to keep travel time – and therefore the cost – down.\n\nItems that can be reused are donated to organisations like Myrorna and Stadsmissionen. This way, your junk removal contributes to a circular economy – items get a new life instead of ending up in landfill.\n\nIf the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Why book junk removal in Danderyd with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We know Danderyd's districts, from the large villa plots of Djursholm and Stocksund to the apartments around Mörby centrum, and that Hagby ÅVC is just to the north. This lets us plan efficient routes and keep the cost down for you.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Stockholm region and the E18 runs through Danderyd. We can often be on site the same day you book – including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharges for any district in Danderyd, and no quote requests. Book in two minutes.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. Your stairwell, your driveway, your walls and your belongings are protected throughout the entire process.",
              },
              {
                label: "Eco-friendly handling –",
                text: "We sort and recycle everything we can via Hagby ÅVC and SÖRAB's facilities. Items suitable for donation are passed on. You may be entitled to a RUT tax deduction for transport to reuse.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does junk removal in Danderyd cost?",
          answer:
            "The price depends on volume, weight and time. Our smallest category (Extra Liten) starts at 249 kr base fare, and an average job costs around 499–1,699 kr depending on the amount. You see the exact price immediately when booking – no hidden fees and no surcharges for any district in Danderyd.",
        },
        {
          question: "Can I get a RUT tax deduction on junk removal in Danderyd?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "How quickly can you come for junk removal in Danderyd?",
          answer:
            "We have vans and staff across the Stockholm region and the E18 runs through Danderyd, so we move quickly between districts. We can often be on site the same day, and for urgent needs sometimes within 60 minutes. Book online and choose the time that suits you.",
        },
        {
          question: "Is a van included with junk removal in Danderyd?",
          answer:
            "Yes, we always come with a van and crew. You don't need to arrange your own vehicle – we handle everything from carrying to transport and disposal at the nearest recycling centre.",
        },
        {
          question: "Do you collect from villas, garages and outbuildings in Danderyd?",
          answer:
            "Yes, a large share of our jobs in Danderyd are villa pickups in Djursholm, Stocksund and Enebyberg. We reverse onto the driveway where possible and clear garages, basements, attics and outbuildings. Just let us know if a gate or garage door needs opening, or if the driveway is gravelled or steep.",
        },
        {
          question: "Can you carry items down from an apartment via stairs in Danderyd?",
          answer:
            "Absolutely. We are used to both stairwells and elevators in the apartment buildings around Mörby centrum, Mörbyskogen and Kevinge. We carry furniture, appliances and other items regardless of floor – no extra charges for stairs.",
        },
        {
          question: "Can you disassemble furniture before removal?",
          answer:
            "Yes, we disassemble furniture on site if needed to get it through doors or down stairs. This is included in the service at no extra cost.",
        },
        {
          question: "Is junk removal in Danderyd insured?",
          answer:
            "Yes, all jobs are insured through Trygg Hansa. This means your property, stairwell, driveway and belongings are protected throughout the entire process – from lifting to transport.",
        },
        {
          question: "Where do you take the waste after junk removal in Danderyd?",
          answer:
            "We sort all materials and drive them to the nearest approved recycling centre. Danderyd has no ÅVC of its own but is a part-owner of SÖRAB, so the waste usually goes to Hagby ÅVC in Täby just to the north. Items that can be reused are donated to Myrorna or Stadsmissionen.",
        },
        {
          question: "Can I book junk removal in Danderyd on weekends?",
          answer:
            "Yes, we offer junk removal every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you directly in our booking.",
        },
        {
          question: "Can you collect items from basements, attics or outbuildings in Danderyd?",
          answer:
            "Yes, we pick up items from all spaces – basements, attics, outbuildings, storage rooms and garages. We are used to the large villa storage rooms in Djursholm and Stocksund as well as the apartment storage units around Mörby.",
        },
        {
          question: "Can you help with junk removal for an estate (dödsbo) in Danderyd?",
          answer:
            "Yes, we often help with clearing and junk removal for estates (dödsbo) and moves in Danderyd's villas. We handle entire houses with care, sort what should be recycled and make sure reusable items are put to good use.",
        },
        {
          question: "Which districts in Danderyd do you cover?",
          answer:
            "We perform junk removal across all of Danderyd – the Danderyd district with Mörby centrum, Mörbyskogen and Kevinge, as well as Djursholm, Stocksund and Enebyberg. No extra charges for any district.",
        },
        {
          question: "Do you handle hazardous waste during junk removal in Danderyd?",
          answer:
            "Unfortunately, we cannot transport hazardous waste such as paint, chemicals, batteries or asbestos. As a Danderyd resident you can drop those off for free at Hagby ÅVC or via the municipality's mobile recycling centre.",
        },
        {
          question: "Which municipalities around Danderyd do you serve?",
          answer:
            "We cover the entire Stockholm region, including Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Book your junk removal in Danderyd in 3 easy steps",
        step2Text:
          "Our young workers in Danderyd come to you and collect whatever you want removed. We handle heavy lifting, tight staircases, narrow elevators and full villa garages and outbuildings – you don't have to lift a finger.",
        ctaHeading: "Book junk removal in Danderyd today",
        ctaText:
          "Fill in the form and get an instant price – no quote requests. Easy junk removal across the whole municipality of Danderyd.",
      },
    },
  },
];
